import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useState } from 'react';
import type { IPhoneNumber } from '../../../extViewmodels';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import { IconPlus, IconX } from './icons';

/**
 * "Manage phone numbers" dialog for company-level numbers (the lead's own
 * numbers, not any contact's). Save dispatches the full updated array via the
 * `useUpdateCompany` mutation in `FocusedLeadView`. Empty `value` rows are
 * dropped before save so the backend doesn't store blanks.
 *
 * Phone metadata (`e164`, `standard`, `countryCode`) is left to the server
 * to recompute on save — that matches how the legacy edit flow works.
 */
export type CompanyPhonesDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: IPhoneNumber[];
	onSave: (phoneNumbers: IPhoneNumber[]) => void;
	saving?: boolean;
};

const PHONE_LABELS = ['Work', 'Mobile', 'Cell', 'Home', 'Direct', 'Office', 'Fax', 'Other'];
const DEFAULT_LABEL = 'Work';

type DraftPhone = { value: string; label: string; metadata?: IPhoneNumber['metadata'] };

const toDraft = (p: IPhoneNumber): DraftPhone => ({
	value: p.value ?? '',
	label: p.label && PHONE_LABELS.includes(p.label) ? p.label : (p.label ?? DEFAULT_LABEL),
	metadata: p.metadata,
});

export const CompanyPhonesDialog: FC<CompanyPhonesDialogProps> = ({ open, onOpenChange, initial, onSave, saving }) => {
	const [drafts, setDrafts] = useState<DraftPhone[]>([]);

	useEffect(() => {
		if (!open) return;
		setDrafts(initial.map(toDraft));
	}, [open, initial]);

	const updateDraft = (index: number, patch: Partial<DraftPhone>) => {
		setDrafts(prev => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
	};
	const removeDraft = (index: number) => {
		setDrafts(prev => prev.filter((_, i) => i !== index));
	};
	const addDraft = () => {
		setDrafts(prev => [...prev, { value: '', label: DEFAULT_LABEL }]);
	};

	const handleSave = () => {
		if (saving) return;
		const cleaned: IPhoneNumber[] = drafts
			.map(d => ({ value: d.value.trim(), label: d.label, metadata: d.metadata }))
			.filter(p => p.value.length > 0);
		onSave(cleaned);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Manage phone numbers</DialogTitle>
				</DialogHeader>
				<DialogBody>
					{drafts.length === 0 ? (
						<div className={css(styles.emptyState)}>No numbers found.</div>
					) : (
						<div className={css(styles.list)}>
							{drafts.map((draft, index) => (
								<div key={index} className={css(styles.row)}>
									<Input
										value={draft.value}
										onChange={e => updateDraft(index, { value: e.target.value })}
										placeholder='(555) 555-0142'
										disabled={saving}
										styles={[styles.input]}
									/>
									<select
										value={draft.label}
										onChange={e => updateDraft(index, { label: e.target.value })}
										disabled={saving}
										className={css(styles.select)}
									>
										{PHONE_LABELS.map(label => (
											<option key={label} value={label}>
												{label}
											</option>
										))}
									</select>
									<button
										type='button'
										className={css(styles.removeBtn)}
										title='Remove phone number'
										aria-label='Remove phone number'
										onClick={() => removeDraft(index)}
										disabled={saving}
									>
										<IconX size={14} />
									</button>
								</div>
							))}
						</div>
					)}
					<button type='button' className={css(styles.addBtn)} onClick={addDraft} disabled={saving}>
						<IconPlus size={14} />
						Add phone number
					</button>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={saving}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	emptyState: {
		padding: '14px 12px',
		textAlign: 'center',
		fontSize: 13,
		color: 'var(--ink-muted)',
		background: 'var(--accent-soft)',
		borderRadius: 8,
		marginBottom: 12,
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		marginBottom: 12,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	input: {
		flex: 1,
		minWidth: 0,
	},
	select: {
		height: 36,
		padding: '0 8px',
		borderRadius: 6,
		border: '1px solid var(--border)',
		background: 'var(--bg)',
		color: 'var(--ink)',
		fontSize: 13,
		fontFamily: 'inherit',
		cursor: 'pointer',
	},
	removeBtn: {
		width: 32,
		height: 32,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: '1px solid var(--border)',
		cursor: 'pointer',
		padding: 0,
		flexShrink: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	addBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 10px',
		borderRadius: 6,
		border: 'none',
		background: 'transparent',
		color: 'var(--accent)',
		fontSize: 13,
		fontFamily: 'inherit',
		cursor: 'pointer',
		':hover': {
			background: 'var(--hover)',
		},
	},
});
